import { Box, Skeleton } from "@mui/material";
import ViewAllButton from "@shared-ui-components/Buttons";
import {
  ErrorState,
  formatNumberWithCommaAndDollar,
  message,
} from "@src/constants";
import { Bar } from "react-chartjs-2";

import Colors from "@constants/Colors";
import { useContext, useEffect, useState } from "react";
import LocationContext from "@src/context/location/locationContext";
import { getCustomExtraPath } from "@pages/Dashboard/RevenueDashBoard/TotalSales/helper";
import { useLocation, useNavigate } from "react-router-dom";
import NoDataAvailable from "@src/components/shared-ui-components/NoDataAvailable";
import { getApiResponse } from "@src/utils/service";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import {
  StyledBox,
  StyledChartHeading,
  StyledRootBox,
  StyledSalesBox,
  StyledSalesRow,
  StyledTitle,
  StyledTypography,
  StyledValue,
} from "./styles";
import {
  alcoholBarchartOptions,
  foodBarChartOptions,
  getRefinedData,
} from "./helper";
import Loader from "../Loader";
import { NetSalesProps, ProdMixData } from "./type";

const defaultProdMixData = {
  netFoodSales: null,
  netAlcoholSales: null,
  topFiveFoods: null,
  topFiveAlcohol: null,
};

const NetSales = ({ title, sales, loading }: NetSalesProps) => {
  let content: React.ReactNode = (
    <StyledValue>
      {sales !== null ? formatNumberWithCommaAndDollar(sales) : ""}
    </StyledValue>
  );

  if (sales === null) content = <ErrorState />;
  if (loading) content = <Skeleton width="70%" />;
  return (
    <StyledSalesBox>
      <StyledTypography>{title}</StyledTypography>
      {content}
    </StyledSalesBox>
  );
};

const ProductMixRevenueBreakdown = () => {
  const [prodMixData, setProdMixData] =
    useState<ProdMixData>(defaultProdMixData);

  const { netFoodSales, netAlcoholSales, topFiveFoods, topFiveAlcohol } =
    prodMixData;

  const [loading, setLoading] = useState(true);
  const [{ locationState }] = useContext(LocationContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const filters = {
    property,
    store,
    rvc: revenueCenter,
    date,
  };

  const filter = getCustomExtraPath(filters);

  const setLocalFilter = (category: string) => {
    localStorage.setItem("ProductFilter", JSON.stringify({ category }));
  };

  const handleNavigate = (category: string) => {
    setLocalFilter(category);
    navigate(`${pathname}/product-mix-breakdown`);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const url = `product-mix${filter}`;
      const { data, error } = await getApiResponse(url);
      setLoading(false);
      if (data !== null) {
        const refinedData = getRefinedData(data);
        setProdMixData(refinedData);
      } else if (data === null) {
        showErrorToastSetState({
          errorMessage: error || "",
          errorTitle: message.ERROR_MESSAGE_DASHBOARD,
          open: true,
        });
      }
    })();
  }, [property, date, revenueCenter, store]);

  const foodMarginData = topFiveFoods?.map((product) => {
    return Math.round(product?.margin);
  });
  const foodCostData = topFiveFoods?.map((product) => {
    return Math.round(product?.cost);
  });
  const alcoholMarginData = topFiveAlcohol?.map((product) => {
    return Math.round(product?.margin);
  });
  const alcoholCostData = topFiveAlcohol?.map((product) => {
    return Math.round(product?.cost);
  });

  const foodChartData = {
    labels: topFiveFoods?.map((product) => product?.name),
    datasets: [
      {
        label: "Margin",
        backgroundColor: foodMarginData?.map((value) =>
          value > 0 ? Colors.secondary : Colors.error_500
        ),
        borderWidth: 1,
        data: foodMarginData,
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
      {
        label: "Cost",
        backgroundColor: foodMarginData?.map((value) =>
          value > 0 ? Colors.primarygray_300 : Colors.error_200
        ),
        borderWidth: 1,
        data: foodCostData,
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
    ],
  };

  const alcoholChartData = {
    labels: topFiveAlcohol?.map((product) => `${product?.name}`),
    datasets: [
      {
        label: "Margin",
        backgroundColor: alcoholMarginData?.map((value) =>
          value > 0 ? Colors.secondary : Colors.error_500
        ),
        borderWidth: 1,
        data: alcoholMarginData,
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
      {
        label: "Cost",
        backgroundColor: alcoholMarginData?.map((value) =>
          value > 0 ? Colors.primarygray_300 : Colors.error_200
        ),
        borderWidth: 1,
        data: alcoholCostData,
        stack: "stack1",
        maxBarThickness: 20,
        barPercentage: 0.5,
        offset: true,
      },
    ],
  };

  let foodContent: React.ReactNode = (
    <Bar
      plugins={[
        {
          id: "1",
          beforeDraw(c) {
            const legends = c?.legend?.legendItems;
            if (legends) {
              legends[0].fillStyle = Colors.secondary;
              legends[1].fillStyle = Colors.primarygray_300;
            }
          },
        },
      ]}
      options={foodBarChartOptions}
      data={foodChartData}
    />
  );
  let alcoholContent: React.ReactNode = (
    <Bar
      plugins={[
        {
          id: "1",
          beforeDraw(c) {
            const legends = c?.legend?.legendItems;
            if (legends) {
              legends[0].fillStyle = Colors.secondary;
              legends[1].fillStyle = Colors.primarygray_300;
            }
          },
        },
      ]}
      options={alcoholBarchartOptions}
      data={alcoholChartData}
    />
  );
  if ((!loading && topFiveFoods === null) || topFiveFoods?.length === 0)
    foodContent = <NoDataAvailable />;
  if ((!loading && topFiveAlcohol === null) || topFiveAlcohol?.length === 0)
    alcoholContent = <NoDataAvailable />;
  if (loading) {
    foodContent = <Loader size={60} />;
    alcoholContent = <Loader size={60} />;
  }

  return (
    <StyledRootBox>
      <Box>
        <StyledTitle color={Colors?.primary}>Product Mix</StyledTitle>
      </Box>

      <>
        <StyledSalesRow>
          <NetSales
            title="Net Food Sales"
            sales={netFoodSales}
            loading={loading}
          />
          <NetSales
            title="Net Alcohol Sales"
            sales={netAlcoholSales}
            loading={loading}
          />
        </StyledSalesRow>
        <StyledChartHeading>Top Five Food with Margin</StyledChartHeading>
        <StyledBox>
          {foodContent}

          <ViewAllButton
            disabled={topFiveFoods === null || topFiveFoods?.length === 0}
            onclick={() => handleNavigate("Food")}
          />
        </StyledBox>
        <StyledChartHeading>Top Five Alcohol with Margin</StyledChartHeading>
        {alcoholContent}
      </>
      <ViewAllButton
        disabled={topFiveAlcohol === null || topFiveAlcohol?.length === 0}
        onclick={() => handleNavigate("Alcohol")}
      />
    </StyledRootBox>
  );
};

export default ProductMixRevenueBreakdown;
